/* Single source of truth for checklist-modal styling.
 *
 * Alignment contract: every visible label/control sits one `sm` inside the
 * modal body's left edge (Mantine Modal default body padding md + an sm
 * inner inset). The row hover background still spans the full body width,
 * so radio circles, checkbox squares, and anchor row content sit `sm` away
 * from the hover background's edge with breathing room. Subheaders, input
 * borders, group labels, title text, and tab text all line up at the same
 * X = md + sm from the modal's outer edge.
 */

/* Outer body — flex column, lg gap between sections. */
.modalBody {
    display: flex;
    flex-direction: column;
    gap: var(--mantine-spacing-lg);
}

/* Section wrapper inside `.modalBody` — subheader + content. */
.modalSection {
    display: flex;
    flex-direction: column;
    gap: var(--mantine-spacing-sm);
}

/* Row list — radio/checkbox rows with zero gap so dibblue-0 hover
 * backgrounds sit flush. */
.modalRowList {
    display: flex;
    flex-direction: column;
}

/* Body paragraph / inline text inside a modal body — sits at the same
 * sm-inset column as subheaders, labels, and radio circles. Use on
 * standalone `<Text>` elements that aren't inside a row or an Alert. */
.modalText {
    margin-inline: var(--mantine-spacing-sm);
}

/* Section header — uppercase, gneis-9, fw 600, size xs. */
.subheader {
    color: var(--mantine-color-gneis-9);
    font-size: var(--mantine-font-size-xs);
    font-weight: 600;
    line-height: 1.5;
    margin-inline: var(--mantine-spacing-sm);
    text-transform: uppercase;
}

/* Mantine Radio.Group / Checkbox.Group `label` slot — same visual treatment
 * as `.subheader`. */
.groupLabel {
    color: var(--mantine-color-gneis-9);
    font-size: var(--mantine-font-size-xs);
    font-weight: 600;
    line-height: 1.5;
    margin: 0 var(--mantine-spacing-sm) var(--mantine-spacing-sm);
    text-transform: uppercase;
}

/* Mantine Modal title slot. */
.modalTitle {
    color: var(--mantine-color-gneis-9);
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.5;
    margin-left: var(--mantine-spacing-sm);
}

/* Modal footer — separator line above buttons, extending to modal edges. */
.modalFooter {
    border-top: 1px solid var(--mantine-color-gneis-1);
    margin-top: var(--mantine-spacing-md);
    margin-inline: calc(var(--mantine-spacing-md) * -1);
    margin-bottom: calc(var(--mantine-spacing-md) * -1);
    padding: var(--mantine-spacing-md)
        calc(var(--mantine-spacing-md) + var(--mantine-spacing-sm));
}

/* Mantine Modal Tabs.List — pulled past the body's md padding so the
 * underline bar spans the modal's full inner width. No own padding;
 * `.modalTab` provides the visual padding inside each tab. `--tabs-color`
 * shadows the default Mantine blue so the active-tab underline matches
 * the rest of the checklist-modal palette without each consumer needing
 * to pass a `color` prop. */
.modalTabsList {
    --tabs-color: var(--mantine-color-dibblue-9);
    margin-inline: calc(var(--mantine-spacing-md) * -1);
    padding-inline: var(--mantine-spacing-sm);
}

/* Mantine Modal Tabs.Tab — md padding so the tab text lines up with the
 * body's natural left edge (list at 0 + tab.padding md = 16 = body padding). */
.modalTab {
    font-size: var(--mantine-font-size-md);
    padding-inline: var(--mantine-spacing-md);
}

.modalTabsPanel {
    padding-top: var(--mantine-spacing-md);
}

/* Clickable row — full-width, dibblue-0 hover. Used as:
 *   - Mantine `Radio` / `Checkbox` root slot (applied to internal divs;
 *     button-reset rules are no-ops there)
 *   - bare `<button>` element (e.g. OpenFillingModal's filling list)
 * Visual sits at the row's left edge (no horizontal padding) so radio
 * circles, checkbox squares, and anchor-row text all align with subheaders.
 */
.modalRow {
    background: transparent;
    border: none;
    border-radius: var(--mantine-radius-sm);
    color: inherit;
    cursor: pointer;
    display: block;
    font: inherit;
    padding: var(--mantine-spacing-xs) var(--mantine-spacing-sm);
    position: relative;
    text-align: left;
    transition: background-color 120ms ease;
    width: 100%;
}

.modalRow:hover {
    background-color: var(--mantine-color-dibblue-0);
}

.modalRowLabelWrapper {
    flex: 1;
}

/* Mantine Radio/Checkbox `label` slot — ::after overlay extends the click
 * target across the row's padding so anywhere in the row toggles. */
.modalRowLabel {
    cursor: pointer;
    display: block;
    width: 100%;
    font-size: 15px;
}

.modalRowLabel::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
}

/* TextInput / NativeSelect root — sm horizontal margin so the input border
 * lines up with subheaders and the radio/checkbox column. */
.selectRoot {
    margin-inline: var(--mantine-spacing-sm);
}

.selectLabel {
    color: var(--mantine-color-gneis-9);
    font-size: var(--mantine-font-size-xs);
    font-weight: 600;
    line-height: 1.5;
    margin-bottom: var(--mantine-spacing-sm);
    text-transform: uppercase;
}

/* Share-modal person row — bare `<button>` list row. Two-line name/email
 * column left, action hint (text + icon) right, hidden until hover/focus.
 * Same geometry vocabulary as `.modalRow` so the text column aligns with
 * subheaders. `.shareRowAdd` gets the dibblue wash (like `.modalRow:hover`),
 * `.shareRowRemove` a red wash for the destructive staging action. */
.shareRow {
    align-items: center;
    background: transparent;
    border: none;
    border-radius: var(--mantine-radius-sm);
    color: inherit;
    cursor: pointer;
    display: flex;
    font: inherit;
    justify-content: space-between;
    padding: var(--mantine-spacing-xs) var(--mantine-spacing-sm);
    text-align: left;
    transition: background-color 120ms ease;
    width: 100%;
}

.shareRowInfo {
    display: flex;
    flex-direction: column;
    min-width: 0; /* enables child ellipsis */
}

.shareRowName {
    color: var(--mantine-color-gneis-9);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.shareRowEmail {
    color: var(--mantine-color-dimmed);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.shareRowHint {
    align-items: center;
    display: none;
    flex-shrink: 0;
    font-size: var(--mantine-font-size-sm);
    gap: var(--mantine-spacing-xs);
    white-space: nowrap;
}

.shareRowHint svg {
    height: 1rem;
    width: 1rem;
}

.shareRow:hover .shareRowHint,
.shareRow:focus-visible .shareRowHint {
    display: flex;
}

.shareRow:hover .shareRowEmail,
.shareRow:focus-visible .shareRowEmail {
    color: var(--mantine-color-gneis-9);
}

.shareRowAdd:hover,
.shareRowAdd:focus-visible {
    background-color: var(--mantine-color-dibblue-0);
}

.shareRowAdd:hover .shareRowName,
.shareRowAdd:hover .shareRowHint,
.shareRowAdd:focus-visible .shareRowName,
.shareRowAdd:focus-visible .shareRowHint {
    color: var(--mantine-color-dibblue-9);
}

.shareRowRemove:hover,
.shareRowRemove:focus-visible {
    background-color: var(--mantine-color-red-0);
}

.shareRowRemove:hover .shareRowName,
.shareRowRemove:hover .shareRowHint,
.shareRowRemove:focus-visible .shareRowName,
.shareRowRemove:focus-visible .shareRowHint {
    color: var(--mantine-color-red-9);
}

/* Modal buttons — shared sizing + typography. */
.primaryButton,
.secondaryButton,
.destructiveButton {
    border-radius: 0.2rem;
    cursor: pointer;
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    padding: 0.35rem 0.75rem;
}

.primaryButton {
    --gen-btn-orange: #f2864f;
    --gen-btn-orange-hover: #ea7738;
    --gen-btn-orange-disabled: #f5b08a;
    background-color: var(--gen-btn-orange);
    border: 1px solid var(--gen-btn-orange);
    color: white;
}

.primaryButton:hover:not(:disabled),
.primaryButton:focus:not(:disabled),
.primaryButton:active:not(:disabled) {
    background-color: var(--gen-btn-orange-hover);
    border-color: var(--gen-btn-orange-hover);
}

.primaryButton:disabled {
    background-color: var(--gen-btn-orange-disabled);
    border-color: var(--gen-btn-orange-disabled);
    cursor: not-allowed;
}

.secondaryButton {
    --gen-btn-outline-disabled: #9a9a9a;
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--mantine-color-gneis-5);
}

.secondaryButton:hover:not(:disabled),
.secondaryButton:focus:not(:disabled),
.secondaryButton:active:not(:disabled) {
    background-color: rgb(0 0 0 / 10%);
    color: var(--mantine-color-gneis-9);
}

.secondaryButton:disabled {
    color: var(--gen-btn-outline-disabled);
    cursor: not-allowed;
}

.destructiveButton {
    --del-btn-red: var(--mantine-color-red-6);
    --del-btn-red-hover: var(--mantine-color-red-7);
    --del-btn-red-disabled: var(--mantine-color-red-3);
    background-color: var(--del-btn-red);
    border: 1px solid var(--del-btn-red);
    color: white;
}

.destructiveButton:hover:not(:disabled),
.destructiveButton:focus:not(:disabled),
.destructiveButton:active:not(:disabled) {
    background-color: var(--del-btn-red-hover);
    border-color: var(--del-btn-red-hover);
}

.destructiveButton:disabled {
    background-color: var(--del-btn-red-disabled);
    border-color: var(--del-btn-red-disabled);
    cursor: not-allowed;
}
